import axios from "axios";
import fileDownload from "js-file-download";
import config from "./config";
import { qrCodeFile } from "./qrCode";
import { auth, getJwtToken } from "./auth";
import { isReloading, leaveUnavailableTeam, teamHeaderFor } from "./workspace";
import * as Sentry from "@sentry/vue";

const axiosInstance = axios.create();

// set default
axiosInstance.defaults.headers.common["Content-Type"] = "application/json";
axiosInstance.defaults.baseURL =
  import.meta.env.MODE === "development"
    ? "http://localhost:3000"
    : config.API_BASE_URL;

/** the selected team goes with every call; the API checks the membership */
export function withTeamHeader(config, uid) {
  const teamId = teamHeaderFor(uid);
  if (teamId) {
    config.headers["X-Team-Id"] = teamId;
  }
  return config;
}

/** the API's answer when the selected team is not the caller's anymore */
export function isNotTeamMemberError(err) {
  return (
    err?.response?.status === 403 &&
    err.response.data?.code === "not_team_member"
  );
}

// set auth token before request
export async function beforeRequest(config) {
  console.log("calling", config.method, config.url, "with", config.data);
  const token = await getJwtToken();
  // a reload is scheduled and the stored selection already is the next page's: sending now
  // would put the call under the wrong workspace (a Viewer's key list, the own plan on a
  // team's overview). The page is about to unload, so the call neither goes out nor settles:
  // a rejection would flash an error. Checked after the await, which a reload can overtake
  if (isReloading()) return new Promise(() => {});
  config.headers.Authorization = "Bearer " + token;
  return withTeamHeader(config, auth().currentUser?.uid);
}
axiosInstance.interceptors.request.use(beforeRequest);

// removed from the selected team (on another device, by an admin): back to the own account
axiosInstance.interceptors.response.use(undefined, (err) => {
  if (isNotTeamMemberError(err)) {
    leaveUnavailableTeam(auth().currentUser?.uid);
  }
  return Promise.reject(err);
});

export async function getPlans() {
  const result = await axiosInstance.get("/paddle/plan");
  return result.data;
}

export async function getUserPlan() {
  const result = await axiosInstance.get("/user/");
  return result.data;
}

export async function getClicksDaily() {
  const result = await axiosInstance.get("/user/clicks-daily");
  return result.data;
}

export async function getOverview(range) {
  const result = await axiosInstance.get("/user/overview", {
    params: { range },
  });
  return result.data;
}

export function handleApiError(err, fallbackMsg = "Something went wrong") {
  const status = err?.response?.status;
  const endpoint = err?.config?.url;
  const extra = {
    responseBody: err?.response?.data,
    requestBody: err?.config?.data,
  };
  if (status >= 500) {
    Sentry.captureException(err, { extra });
  } else if (!status) {
    Sentry.captureException(err, { level: "warning", extra });
  } else {
    // 4xx (plan limits, validation, short-handle collisions, ...) was previously
    // dropped entirely, so real failure volume at endpoints like link creation
    // was invisible. Keep it, at a level/fingerprint that won't drown out 5xx.
    Sentry.captureException(err, {
      level: "info",
      tags: { endpoint, status },
      fingerprint: ["api-4xx", endpoint, String(status)],
      extra,
    });
  }
  return err.response?.data?.message || err.message || fallbackMsg;
}

export async function initAccount() {
  const result = await axiosInstance.post("/user/signup");
  return result.data;
}

export async function movePlan(name, churnData, billingCycle = "monthly") {
  try {
    const result = await axiosInstance.post(
      "/paddle/move/" + name + "?billingCycle=" + billingCycle,
      churnData,
    );
    return result.data;
  } catch (err) {
    console.log(err);
    return {
      success: false,
    };
  }
}

export async function renewPlan() {
  try {
    const result = await axiosInstance.post("/paddle/renew");
    return result.data;
  } catch (err) {
    console.log(err);
    return {
      success: false,
    };
  }
}

export async function listTxs() {
  const result = await axiosInstance.get("/paddle/transactions");
  return result.data;
}

export async function getPaddleSubscription() {
  const result = await axiosInstance.get("/paddle/subscription");
  return result.data;
}

export async function newLink(data) {
  const result = await axiosInstance.post("/link", data);
  return result;
}

export async function updateLink(id, data) {
  const result = await axiosInstance.put("/link/id/" + id, data);
  return result;
}

export async function deleteLink(id) {
  const result = await axiosInstance.delete("/link/id/" + id);
  return result;
}

export async function downloadQrCodeOfLink(fullUrl, format = "png-large") {
  try {
    const { blob, filename } = await qrCodeFile(fullUrl, format);
    fileDownload(blob, filename);
  } catch (error) {
    console.error("Error downloading QR code:", error);
    throw error;
  }
}

export async function listLinks(ops) {
  const query = {};
  if (ops.keyword) {
    query.keyword = ops.keyword;
  } else if (ops.take) {
    query.skip = ops.skip;
    query.take = ops.take;
  }
  console.log("query to send", query);
  const result = await axiosInstance.get("/link", { params: query });
  return result.data;
}

export async function getLinkByLinkId(id) {
  const result = await axiosInstance.get("/link/id/" + id);
  return result.data;
}

export async function getDomains(includePublic = false) {
  const query = {
    includePublic,
  };
  const result = await axiosInstance.get("/domains", { params: query });
  return result.data;
}

/** `allowInUse`: the user confirmed an address that already points somewhere */
export async function createCustomDomain(domain, allowInUse = false) {
  const result = await axiosInstance.post(
    "/domains",
    allowInUse ? { domain, allowInUse: true } : { domain },
  );
  return result.data;
}

export async function getCustomDomainStatus(domainId) {
  const encodedDomainId = encodeURIComponent(domainId);
  const result = await axiosInstance.get(`/domains/${encodedDomainId}/status`);
  return result.data;
}

export async function createAliasUrl(domainId, path, redirectUrl) {
  const result = await axiosInstance.post("/domains/" + domainId + "/alias", {
    path: "/" + (path ? path : ""),
    redirect_url: redirectUrl,
  });
  return result.data;
}

export async function updateAliasUrl(domainId, path, redirectUrl) {
  const result = await axiosInstance.put("/domains/" + domainId + "/alias", {
    path: "/" + (path ? path : ""),
    redirect_url: redirectUrl,
  });
  return result.data;
}

export async function deleteAliasUrl(domainId, path) {
  const result = await axiosInstance.delete("/domains/" + domainId + "/alias", {
    data: { path },
  });
  return result.data;
}

export async function listApiKeys() {
  const result = await axiosInstance.get("/user/api-key");
  return result.data;
}

export async function deleteApiKey(id) {
  const result = await axiosInstance.delete("/user/api-key/" + id);
  return result.data;
}

export async function createApiKey(note) {
  const dataToUse =
    typeof note === "string" ? { description: note } : undefined;
  const result = await axiosInstance.post("/user/api-key/", dataToUse);
  return result.data;
}

/**
 * used to notify email change or display name changes
 */
export async function notifyEmailVerified() {
  const result = await axiosInstance.put("/user/email");
  return result.data;
}

export async function updateUserMetadata(data) {
  try {
    const result = await axiosInstance.put("/user/metadata", data);
    return result.data;
  } catch (err) {
    console.log(err);
    return;
  }
}

export async function getCustomerPortalUrl() {
  const result = await axiosInstance.get("/paddle/portal-url");
  return result.data;
}

export async function getBillingEmail() {
  const result = await axiosInstance.get("/paddle/billing-email");
  return result.data;
}

export async function updateBillingEmail(email) {
  const result = await axiosInstance.put("/paddle/billing-email", { email });
  return result.data;
}

export async function refreshLandingPage(linkId) {
  const result = await axiosInstance.put(
    "/link/id/" + linkId + "/landing_page",
  );
  return result.data;
}

export async function getDeepLinkingDomains() {
  const result = await axiosInstance.get("/deeplinking-domains");
  return result.data;
}

export async function createDeepLinkingConfig(config) {
  const result = await axiosInstance.post("/deeplinking-domains", config);
  return result.data;
}

export async function updateDeepLinkingConfig(domain, config) {
  const result = await axiosInstance.put(
    `/deeplinking-domains/${encodeURIComponent(domain)}`,
    config,
  );
  return result.data;
}

export async function deleteDeepLinkingConfig(domain) {
  const result = await axiosInstance.delete(
    `/deeplinking-domains/${encodeURIComponent(domain)}`,
  );
  return result.data;
}

export async function getTeamMembers() {
  const result = await axiosInstance.get("/team/members");
  return result.data;
}

export async function renameTeam(name) {
  const result = await axiosInstance.put("/team/", { name });
  return result.data;
}

/** inviting an address already invited sends the invitation again */
export async function inviteToTeam(email, role) {
  const result = await axiosInstance.post("/team/invitations", { email, role });
  return result.data;
}

export async function revokeInvitation(id) {
  const result = await axiosInstance.delete("/team/invitations/" + id);
  return result.data;
}

export async function changeMemberRole(userId, role) {
  const result = await axiosInstance.put(
    "/team/members/" + encodeURIComponent(userId),
    { role },
  );
  return result.data;
}

/** also how a person leaves: their own uid */
export async function removeMember(userId) {
  const result = await axiosInstance.delete(
    "/team/members/" + encodeURIComponent(userId),
  );
  return result.data;
}

/** no sign-in: the token is the proof, and the person may not have an account yet */
export async function lookupInvitation(token) {
  const result = await axios.post(
    axiosInstance.defaults.baseURL + "/team/invitations/lookup",
    {
      token,
    },
  );
  return result.data;
}

export async function acceptInvitation(token) {
  const result = await axiosInstance.post("/team/invitations/accept", {
    token,
  });
  return result.data;
}
